import React, { useState, useEffect, useRef, useCallback } from 'react';
import { PenSquare, X, Check, Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';
import { useAuthStore, AuthUser } from '../../stores/useAuthStore';
import { useCurrencyStore } from '../../stores/useCurrencyStore';
import { SupportedCurrency } from '../../types';
import { lockScroll, unlockScroll } from '../../utils/scrollLock';

export interface EditProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  onProfileUpdated?: (updatedUser: AuthUser) => void;
}

export const EditProfileModal: React.FC<EditProfileModalProps> = ({
  isOpen,
  onClose,
  onProfileUpdated,
}) => {
  const user = useAuthStore((s) => s.user);
  const token = useAuthStore((s) => s.token);
  const setUser = useAuthStore((s) => s.setUser);

  const currentCurrency = useCurrencyStore((s) => s.currency);
  const setCurrency = useCurrencyStore((s) => s.setCurrency);

  // Form state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phonePrefix, setPhonePrefix] = useState<'+54' | '+52'>('+54');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [preferredCurrency, setPreferredCurrency] = useState<SupportedCurrency>('ARS');

  // UI state
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isClosing, setIsClosing] = useState(false);

  const handleClose = useCallback(() => {
    if (isClosing) return;
    setIsClosing(true);
    unlockScroll();
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 160);
  }, [isClosing, onClose]);

  const previouslyFocusedElementRef = useRef<HTMLElement | null>(null);

  // Sync form values whenever modal opens or user/currency updates
  useEffect(() => {
    if (isOpen) {
      previouslyFocusedElementRef.current = document.activeElement as HTMLElement;
      lockScroll();
      setIsClosing(false);

      setFirstName(user?.first_name || '');
      setLastName(user?.last_name || '');

      const rawPhone = (user?.phone || '').trim();
      if (rawPhone.startsWith('+52')) {
        setPhonePrefix('+52');
        setPhoneNumber(rawPhone.replace(/^\+52\s*/, ''));
      } else if (rawPhone.startsWith('+54')) {
        setPhonePrefix('+54');
        setPhoneNumber(rawPhone.replace(/^\+54\s*/, ''));
      } else {
        setPhonePrefix('+54');
        setPhoneNumber(rawPhone);
      }

      setPreferredCurrency(currentCurrency || 'ARS');
      setErrorMessage(null);
      setSuccessMessage(null);
      setIsLoading(false);
    } else {
      unlockScroll();
      if (previouslyFocusedElementRef.current) {
        previouslyFocusedElementRef.current.focus();
      }
    }

    return () => {
      unlockScroll();
    };
  }, [isOpen, user, currentCurrency]);

  // Escape key listener
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        handleClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, handleClose]);

  if (!isOpen && !isClosing) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);
    setSuccessMessage(null);
    setIsLoading(true);

    const authToken =
      token ||
      localStorage.getItem('fn_token') ||
      localStorage.getItem('token') ||
      localStorage.getItem('auth_token');

    // Limpieza de duplicados al escribir o guardar
    let cleanNumber = phoneNumber.replace(/[^\d\s-]/g, '').trim();
    if (phonePrefix === '+52' && /^52\s*/.test(cleanNumber)) {
      cleanNumber = cleanNumber.replace(/^52\s*/, '');
    } else if (phonePrefix === '+54' && /^54\s*/.test(cleanNumber)) {
      cleanNumber = cleanNumber.replace(/^54\s*/, '');
    }

    const digitsOnly = cleanNumber.replace(/\D/g, '');
    if (digitsOnly.length > 0 && (digitsOnly.length < 8 || digitsOnly.length > 13)) {
      setErrorMessage('El número de teléfono ingresado no parece válido. Verifica no repetir el código de país.');
      return;
    }

    const fullPhone = digitsOnly ? `${phonePrefix} ${cleanNumber}` : '';

    const payload = {
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      phone: fullPhone,
    };

    try {
      // 1. Sync preferred currency in localStorage and UI store
      if (preferredCurrency) {
        localStorage.setItem('fn_currency', preferredCurrency);
        if (preferredCurrency !== currentCurrency) {
          setCurrency(preferredCurrency);
        }
      }

      // 2. Consume backend endpoint PUT /api/auth/me/profile
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
      }

      const res = await fetch('/api/auth/me/profile', {
        method: 'PUT',
        headers,
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Error al actualizar el perfil en el servidor.');
      }

      const updatedUserOut = await res.json();

      // 3. Update auth store with updated profile
      const updatedUser: AuthUser = {
        ...(user || {}),
        ...updatedUserOut,
        first_name: payload.first_name,
        last_name: payload.last_name,
        phone: payload.phone,
      };
      setUser(updatedUser);

      // Trigger cross-window synchronization events
      window.dispatchEvent(new Event('userChanged'));
      window.dispatchEvent(new Event('storage'));

      if (onProfileUpdated) {
        onProfileUpdated(updatedUser);
      }

      setSuccessMessage('¡Perfil actualizado con éxito!');

      // Close modal gracefully after feedback
      setTimeout(() => {
        setIsLoading(false);
        handleClose();
      }, 600);
    } catch (err: any) {
      setIsLoading(false);
      setErrorMessage(err.message || 'Ocurrió un error inesperado al guardar los cambios.');
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="edit-profile-title"
      className={`fixed inset-0 z-[200000] soft-backdrop flex items-center justify-center p-4 overscroll-contain select-none ${
        isClosing ? 'anim-backdrop-out' : 'anim-backdrop-in'
      }`}
      onClick={(e) => {
        if (e.target === e.currentTarget) handleClose();
      }}
    >
      {/* Outer Chamfered Shell with drop shadow */}
      <div
        className={`w-full max-w-md bg-white text-[#06173f] p-5 sm:p-6 border-2 border-[var(--line)] shadow-2xl relative space-y-4 ${
          isClosing ? 'anim-modal-out' : 'anim-modal-in'
        }`}
        style={{
          clipPath: 'var(--g-cut)',
          filter: 'drop-shadow(8px 8px 0 rgba(6,23,63,0.25))',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header Bar */}
        <div className="flex items-center justify-between border-b-2 border-[var(--line)] pb-3">
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 bg-[var(--ice)] text-[#1447e6] flex items-center justify-center font-bold text-base shrink-0"
              style={{
                clipPath: 'var(--cut-sm)',
              }}
            >
              <PenSquare className="w-5 h-5 text-[#1447e6]" />
            </div>
            <div>
              <h3
                id="edit-profile-title"
                className="text-base sm:text-lg font-black uppercase text-[#06173f] font-heading tracking-wide leading-tight"
              >
                Modificar Perfil
              </h3>
              <p className="text-xs text-[var(--soft)] font-medium">
                Actualiza tus datos de contacto
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleClose}
            aria-label="Cerrar modal"
            className="w-8 h-8 rounded-lg bg-[var(--ice)] hover:bg-[#e2e8f0] text-[var(--soft)] hover:text-[#06173f] flex items-center justify-center transition-colors cursor-pointer"
            title="Cerrar"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Feedback Banners */}
        {errorMessage && (
          <div className="p-3 bg-red-500/10 border-2 border-red-500/30 text-red-700 text-xs font-bold rounded-none flex items-center gap-2">
            <AlertCircle className="w-4 h-4 shrink-0 text-red-600" />
            <span>{errorMessage}</span>
          </div>
        )}

        {successMessage && (
          <div className="p-3 bg-emerald-500/10 border-2 border-emerald-500/30 text-emerald-700 text-xs font-bold rounded-none flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 shrink-0 text-emerald-600" />
            <span>{successMessage}</span>
          </div>
        )}

        {/* Form Body */}
        <form onSubmit={handleSubmit} className="space-y-3.5 text-left">
          {/* Nombre */}
          <div>
            <label
              htmlFor="edit-first-name"
              className="text-xs font-bold uppercase tracking-wider text-[var(--soft)] block mb-1 font-heading"
            >
              Nombre
            </label>
            <input
              id="edit-first-name"
              type="text"
              maxLength={50}
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="Tu nombre"
              className="w-full bg-white border-2 border-[var(--line)] px-3.5 py-2.5 text-xs sm:text-sm font-bold text-[#06173f] placeholder-slate-400 focus:outline-none focus:border-[#1447e6] transition"
              style={{
                clipPath:
                  'polygon(0 0, calc(100% - 6px) 0, 100% 6px, 100% 100%, 6px 100%, 0 calc(100% - 6px))',
              }}
            />
          </div>

          {/* Apellido */}
          <div>
            <label
              htmlFor="edit-last-name"
              className="text-xs font-bold uppercase tracking-wider text-[var(--soft)] block mb-1 font-heading"
            >
              Apellido
            </label>
            <input
              id="edit-last-name"
              type="text"
              maxLength={50}
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="Tu apellido"
              className="w-full bg-white border-2 border-[var(--line)] px-3.5 py-2.5 text-xs sm:text-sm font-bold text-[#06173f] placeholder-slate-400 focus:outline-none focus:border-[#1447e6] transition"
              style={{
                clipPath:
                  'polygon(0 0, calc(100% - 6px) 0, 100% 6px, 100% 100%, 6px 100%, 0 calc(100% - 6px))',
              }}
            />
          </div>

          {/* Número de Teléfono */}
          <div>
            <label
              htmlFor="edit-phone"
              className="text-xs font-bold uppercase tracking-wider text-[var(--soft)] block mb-1 font-heading"
            >
              Número de Teléfono{' '}
              <span className="text-[var(--soft)] font-normal lowercase">(opcional)</span>
            </label>
            <div className="flex items-center gap-2">
              <div className="relative w-36 shrink-0">
                <select
                  id="edit-phone-prefix"
                  value={phonePrefix}
                  onChange={(e) => setPhonePrefix(e.target.value as '+54' | '+52')}
                  className="w-full bg-white border-2 border-[var(--line)] px-2.5 py-2.5 text-xs font-bold text-[#06173f] focus:outline-none focus:border-[#1447e6] transition cursor-pointer"
                  style={{
                    clipPath:
                      'polygon(0 0, calc(100% - 6px) 0, 100% 6px, 100% 100%, 6px 100%, 0 calc(100% - 6px))',
                  }}
                >
                  <option value="+54">🇦🇷 +54 (AR)</option>
                  <option value="+52">🇲🇽 +52 (MX)</option>
                </select>
              </div>
              <input
                id="edit-phone"
                type="tel"
                maxLength={25}
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder="Ej: 9 11 1234-5678"
                className="flex-1 min-w-0 bg-white border-2 border-[var(--line)] px-3.5 py-2.5 text-xs sm:text-sm font-bold font-mono text-[#06173f] placeholder-slate-400 focus:outline-none focus:border-[#1447e6] transition"
                style={{
                  clipPath:
                    'polygon(0 0, calc(100% - 6px) 0, 100% 6px, 100% 100%, 6px 100%, 0 calc(100% - 6px))',
                }}
              />
            </div>
          </div>

          {/* Moneda Preferida */}
          <div>
            <label
              htmlFor="edit-currency"
              className="text-xs font-bold uppercase tracking-wider text-[var(--soft)] block mb-1 font-heading"
            >
              Moneda Preferida
            </label>
            <div className="relative w-full">
              <select
                id="edit-currency"
                value={preferredCurrency}
                onChange={(e) => setPreferredCurrency(e.target.value as SupportedCurrency)}
                className="w-full bg-white border-2 border-[var(--line)] px-3 py-2.5 text-xs font-bold text-[#06173f] focus:outline-none focus:border-[#1447e6] transition cursor-pointer"
                style={{
                  clipPath:
                    'polygon(0 0, calc(100% - 6px) 0, 100% 6px, 100% 100%, 6px 100%, 0 calc(100% - 6px))',
                }}
              >
                <option value="ARS">🇦🇷 ARS (Argentina)</option>
                <option value="MXN">🇲🇽 MXN (México)</option>
              </select>
            </div>
          </div>

          {/* Submit Action Button */}
          <button
            id="btn-save-profile"
            type="submit"
            disabled={isLoading}
            className="w-full bg-[#1447e6] hover:bg-[#0a2a9c] disabled:opacity-50 text-white font-heading font-black py-3 sm:py-3.5 uppercase tracking-wider text-xs sm:text-sm transition flex items-center justify-center gap-2 shadow-lg cursor-pointer mt-2"
            style={{
              clipPath:
                'polygon(0 0, calc(100% - 8px) 0, 100% 8px, 100% 100%, 8px 100%, 0 calc(100% - 8px))',
            }}
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin text-white" />
                <span>Guardando Cambios...</span>
              </>
            ) : (
              <>
                <span>Guardar Cambios</span>
                <Check className="w-4 h-4 stroke-[2.5]" />
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default EditProfileModal;
